/* eslint-disable import/no-unused-modules */
import { atom, useSetAtom } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import { useSharedTiles } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import type { RefObject } from 'react';
import { useCallback, useEffect } from 'react';
import type { OnChangeSlideEvent } from '@rocco/components/carousel';

export type ProductPageTrackingSection =
    | 'ProductPageProducts'
    | 'ProductPageProfessionals'
    | 'ProductPageProjects';

/**
 * Context interface for managing product page tracking actions and interactions.
 * @interface ProductPageTrackingContext
 */
interface ProductPageTrackingContext {
    onProductsCTAClick?: () => void;
    onCategorySliderSlideChange?: (
        section: ProductPageTrackingSection,
        data: OnChangeSlideEvent,
    ) => void;
}

/**
 * Atoms for managing product page tracking context.
 * `localContextAtom` stores the core context data privately.
 * `remixProductPageTrackingContext` provides read-only access to the context data.
 */
export const remixProductPageTrackingContext = atom((get) =>
    get(localContextAtom),
);
const localContextAtom = atom<ProductPageTrackingContext>({});

/**
 * Hook for tracking events on the product landing page.
 * Adds tracking to all tiles on the product page using the shared tiles hook.
 * @returns {Object} An object containing the tracking configuration.
 */
export const useProductPageTracking = () => {
    const tracker = useTracker();
    const setContext = useSetAtom(localContextAtom);

    /**
     * Tracks when a user clicks a category link on the product landing page.
     * Logs the event with the current URL and category ID.
     * @param categoryID - The ID of the category that was clicked
     */
    const onCategoryClick = useCallback(
        (categoryID: number) => {
            tracker.log('PPCategoryClick', {
                url: new URL(window.location.href),
                data: {
                    ExtraData: categoryID,
                },
            });
        },
        [tracker],
    );

    const onProductsCTAClick = useCallback(() => {
        tracker.log('PPCategoryViewAllClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: 'ProductPageProducts',
            },
        });
    }, [tracker]);

    /**
     * Tracks when a user clicks on a tile (product, professional, project etc) on the product landing page.
     * Logs the event with the current URL and item details.
     * @param targetRef - Reference to the clicked tile element
     * @param itemID - Optional ID of the library item (product, project etc)
     * @param professionalID - Optional ID of the associated professional
     */
    const onTileClick = useCallback(
        (
            targetRef: RefObject<HTMLElement | null>,
            itemID?: number,
            professionalID?: number,
        ) => {
            tracker.log('PPTileClick', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: itemID,
                    ProfessionalID: professionalID,
                },
            });
        },
        [tracker],
    );

    const onTileMediaSliderSlideChange = useCallback(
        (direction: 'CarouselPrev' | 'CarouselNext', itemID?: number) => {
            tracker.log(direction, {
                url: new URL(window.location.href),
                data: {
                    Source: 'ProductPage',
                    ExtraData: {
                        LibraryItemID: itemID,
                    },
                },
            });
        },
        [tracker],
    );

    /**
     * Tracks when a user slides a category slider on the product landing page.
     * Logs the event with the current URL and section information.
     * @param section - The section of the product page where the slide occurred
     * @param data - The slide event data including current index
     */
    const onCategorySliderSlideChange = useCallback(
        (section: ProductPageTrackingSection, data: OnChangeSlideEvent) => {
            tracker.log('PPCategorySliderSlideChange', {
                url: new URL(window.location.href),
                data: {
                    ExtraData: JSON.stringify({ data, section }),
                },
            });
        },
        [tracker],
    );

    // Add tracking to all tiles on the product page using the shared tiles hook
    useSharedTiles({
        trackingConfig: {
            onClick: onTileClick,
            onCategoryClick,
            onTileMediaSliderSlideChange,
        },
    });

    // set the context for the product page tracking (remix related)
    useEffect(() => {
        setContext({
            onProductsCTAClick,
            onCategorySliderSlideChange,
        });
    }, [setContext, onProductsCTAClick, onCategorySliderSlideChange]);
};
